import type { VideoDisplayRect } from './poi-projection'
import type { PayloadInteractionRect } from './payload-video-interaction'
import type { DrcAiSelection } from '../types/drc'

export enum DjiAiObjectType {
  Invalid = 0,
  Unknown = 1,
  Person = 2,
  Car = 3,
  Boat = 4,
}

export enum DjiAiTargetState {
  Invalid = 0,
  Tracked = 1,
  VisionBeaconFusioned = 2,
  AuxiliaryTracked = 3,
  NotConfident = 4,
  LostWithPredict = 5,
  Lost = 6,
  Redetected = 7,
  CommonVirtualPoint = 8,
}

export interface DjiAiTarget {
  id: number;
  type: DjiAiObjectType;
  state: DjiAiTargetState;
  centerX: number;
  centerY: number;
  width: number;
  height: number;
  distanceMm: number;
}

export interface DjiAiTypeCount {
  type: number;
  count: number;
}

export interface DjiAiRecognitionFrame {
  version: number;
  timestamp: number;
  valid: boolean;
  trackId: number;
  targets: DjiAiTarget[];
  counts: DjiAiTypeCount[];
  littleEndian: boolean;
}

export interface ProjectedDjiAiTarget {
  left: number;
  top: number;
  width: number;
  height: number;
}

const TARGET_GROUP_TYPE = 10
const COUNT_GROUP_TYPE = 12
const TARGET_SIZE = 16
const COUNT_SIZE = 3
const NORMALIZED_COORDINATE_MAX = 10000

export const RIGHT_BOX_DRAG_THRESHOLD_PX = 6

function clamp (value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

function readUint16 (data: Uint8Array, offset: number, littleEndian: boolean) {
  return new DataView(data.buffer, data.byteOffset, data.byteLength).getUint16(offset, littleEndian)
}

function readUint32 (data: Uint8Array, offset: number, littleEndian: boolean) {
  return new DataView(data.buffer, data.byteOffset, data.byteLength).getUint32(offset, littleEndian)
}

function isSupportedTargetType (type: number): type is DjiAiObjectType {
  return type === DjiAiObjectType.Person || type === DjiAiObjectType.Car || type === DjiAiObjectType.Boat
}

function parseTargetPayload (payload: Uint8Array, littleEndian: boolean): DjiAiRecognitionFrame | null {
  if (payload.byteLength < 10) {
    return null
  }
  const version = payload[0]
  const timestamp = readUint32(payload, 1, littleEndian)
  const frameType = payload[5]
  if (frameType !== 0 && frameType !== 1) {
    return null
  }

  let offset = 6
  if (frameType === 1) {
    if (payload.byteLength < offset + 12 + 4) {
      return null
    }
    offset += 12
  }
  if (payload.byteLength < offset + 4) {
    return null
  }

  const trackId = readUint16(payload, offset, littleEndian)
  offset += 2
  offset += 1 // reserved2
  const groupCount = payload[offset]
  offset += 1
  const targets: DjiAiTarget[] = []
  const counts: DjiAiTypeCount[] = []

  for (let groupIndex = 0; groupIndex < groupCount; groupIndex += 1) {
    if (offset + 2 > payload.byteLength) {
      return null
    }
    const groupType = payload[offset]
    const elementCount = payload[offset + 1]
    offset += 2

    if (groupType === TARGET_GROUP_TYPE) {
      const groupLength = elementCount * TARGET_SIZE
      if (offset + groupLength > payload.byteLength) {
        return null
      }
      for (let index = 0; index < elementCount; index += 1) {
        const type = payload[offset + 2]
        const state = payload[offset + 3]
        const centerX = readUint16(payload, offset + 4, littleEndian)
        const centerY = readUint16(payload, offset + 6, littleEndian)
        const width = readUint16(payload, offset + 8, littleEndian)
        const height = readUint16(payload, offset + 10, littleEndian)
        if (
          centerX > NORMALIZED_COORDINATE_MAX ||
          centerY > NORMALIZED_COORDINATE_MAX ||
          width > NORMALIZED_COORDINATE_MAX ||
          height > NORMALIZED_COORDINATE_MAX ||
          state > DjiAiTargetState.CommonVirtualPoint
        ) {
          return null
        }
        if (isSupportedTargetType(type)) {
          targets.push({
            id: readUint16(payload, offset, littleEndian),
            type,
            state,
            centerX,
            centerY,
            width,
            height,
            distanceMm: readUint32(payload, offset + 12, littleEndian),
          })
        }
        offset += TARGET_SIZE
      }
      continue
    }

    if (groupType === COUNT_GROUP_TYPE) {
      const groupLength = elementCount * COUNT_SIZE
      if (offset + groupLength > payload.byteLength) {
        return null
      }
      for (let index = 0; index < elementCount; index += 1) {
        counts.push({
          type: payload[offset],
          count: readUint16(payload, offset + 1, littleEndian),
        })
        offset += COUNT_SIZE
      }
      continue
    }

    // The protocol has no byte length for unknown groups, so later groups
    // cannot be located safely. Keep the known data parsed before this group.
    break
  }

  return {
    version,
    timestamp,
    valid: frameType === 1,
    trackId,
    targets,
    counts,
    littleEndian,
  }
}

interface PayloadCandidate {
  payload: Uint8Array;
  littleEndian: boolean;
}

function findTypeSevenPayloads (data: Uint8Array): PayloadCandidate[] {
  const candidates: PayloadCandidate[] = []
  for (let offset = 0; offset + 4 <= data.byteLength; offset += 1) {
    for (const littleEndian of [true, false]) {
      if (readUint16(data, offset, littleEndian) !== 7) {
        continue
      }
      const length = readUint16(data, offset + 2, littleEndian)
      const payloadStart = offset + 4
      const payloadEnd = payloadStart + length
      if (length >= 10 && payloadEnd <= data.byteLength) {
        candidates.push({
          payload: data.subarray(payloadStart, payloadEnd),
          littleEndian,
        })
      }
    }
  }
  return candidates
}

export function parseDjiAiSei (data: Uint8Array | null | undefined): DjiAiRecognitionFrame | null {
  if (!data || data.byteLength < 4) {
    return null
  }
  for (const candidate of findTypeSevenPayloads(data)) {
    const preferred = parseTargetPayload(candidate.payload, candidate.littleEndian)
    if (preferred) {
      return preferred
    }
    const fallback = parseTargetPayload(candidate.payload, !candidate.littleEndian)
    if (fallback) {
      return fallback
    }
  }
  return null
}

export function shouldRenderDjiAiTarget (target: DjiAiTarget) {
  return isSupportedTargetType(target.type) && ![
    DjiAiTargetState.Invalid,
    DjiAiTargetState.Lost,
    DjiAiTargetState.CommonVirtualPoint,
  ].includes(target.state)
}

export function isPredictedDjiAiTarget (target: DjiAiTarget) {
  return target.state === DjiAiTargetState.NotConfident || target.state === DjiAiTargetState.LostWithPredict
}

export function isTrackedDjiAiTarget (
  target: DjiAiTarget,
  trackId: number,
  trackingActive: boolean
) {
  return trackingActive && trackId > 0 && target.id === trackId
}

export function projectDjiAiTarget (
  target: DjiAiTarget,
  videoRect: VideoDisplayRect
): ProjectedDjiAiTarget {
  const normalizedWidth = clamp(target.width / NORMALIZED_COORDINATE_MAX, 0, 1)
  const normalizedHeight = clamp(target.height / NORMALIZED_COORDINATE_MAX, 0, 1)
  const rawLeft = target.centerX / NORMALIZED_COORDINATE_MAX - normalizedWidth / 2
  const rawTop = target.centerY / NORMALIZED_COORDINATE_MAX - normalizedHeight / 2
  const normalizedLeft = clamp(rawLeft, 0, 1)
  const normalizedTop = clamp(rawTop, 0, 1)
  const right = clamp(rawLeft + normalizedWidth, 0, 1)
  const bottom = clamp(rawTop + normalizedHeight, 0, 1)
  return {
    left: videoRect.left + normalizedLeft * videoRect.width,
    top: videoRect.top + normalizedTop * videoRect.height,
    width: (right - normalizedLeft) * videoRect.width,
    height: (bottom - normalizedTop) * videoRect.height,
  }
}

export function isRightBoxDrag (
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  thresholdPx = RIGHT_BOX_DRAG_THRESHOLD_PX
) {
  return Math.hypot(endX - startX, endY - startY) >= thresholdPx
}

export function toDrcAiSelection (rect: PayloadInteractionRect): DrcAiSelection {
  const toDeviceCoordinate = (value: number) => Math.round(clamp(value, 0, 1) * NORMALIZED_COORDINATE_MAX)
  return {
    center_x: toDeviceCoordinate(rect.x + rect.width / 2),
    center_y: toDeviceCoordinate(rect.y + rect.height / 2),
    width: toDeviceCoordinate(rect.width),
    height: toDeviceCoordinate(rect.height),
  }
}
